import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Shield, ArrowLeft, Calendar, DollarSign, User, 
  Building2, AlertCircle, FileText, Image, 
  CheckCircle, XCircle, Clock, Edit, Mail, Download, Upload, Mic,
  FileSearch
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import SubmissionDocumentTab from './SubmissionDocumentTab';
import PolicyAnalysisTab from './PolicyAnalysisTab';
import DisputeNarrativeTab from './DisputeNarrativeTab';
import CarrierDocumentsTab from './CarrierDocumentsTab';
import ImageLightbox from '../ui/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

async function chunkedUploadToS3(file, folder) {
  const initForm = new FormData();
  initForm.append('file_name', file.name);
  initForm.append('folder', folder);
  const initRes = await axios.post(`${BACKEND_URL}/api/upload/initiate-multipart`, initForm);
  const { upload_id, s3_key } = initRes.data;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const parts = [];
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
    const chunkForm = new FormData();
    chunkForm.append('file', chunk, `chunk_${i + 1}`);
    chunkForm.append('upload_id', upload_id);
    chunkForm.append('s3_key', s3_key);
    chunkForm.append('part_number', String(i + 1));
    const partRes = await axios.post(`${BACKEND_URL}/api/upload/upload-chunk`, chunkForm);
    parts.push(partRes.data);
  }
  const completeForm = new FormData();
  completeForm.append('upload_id', upload_id);
  completeForm.append('s3_key', s3_key);
  completeForm.append('parts_json', JSON.stringify(parts));
  completeForm.append('original_filename', file.name);
  const res = await axios.post(`${BACKEND_URL}/api/upload/complete-multipart`, completeForm);
  return res.data;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ClaimDetail = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showDenialSection, setShowDenialSection] = useState(false);
  const [denialForm, setDenialForm] = useState({
    denial_reason: '',
    adjuster_feedback: '',
    justification_strategy: ''
  });
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
    include_photos: true
  });
  const [calculatorForm, setCalculatorForm] = useState({
    covered_work_amount: 0,
    deductible: 0,
    depreciation: 0,
    rcv: 0,
    acv: 0,
    insurance_payment: 0,
    homeowner_responsibility: 0
  });
  const [claimLightboxImages, setClaimLightboxImages] = useState([]);
  const [claimLightboxIndex, setClaimLightboxIndex] = useState(0);

  useEffect(() => {
    loadClaim();
    loadPhotos();
  }, [claimId]);

  useEffect(() => {
    if (claim) {
      // Initialize calculator with claim values
      const estimateTotal = claim.estimate?.total_cost || 0;
      const covered = claim.covered_work_amount || estimateTotal;
      const deductible = claim.deductible || 0;
      const depreciation = claim.depreciation || 0;
      
      setCalculatorForm({
        covered_work_amount: covered,
        deductible: deductible,
        depreciation: depreciation,
        rcv: covered,
        acv: covered - depreciation,
        insurance_payment: covered - deductible - depreciation,
        homeowner_responsibility: deductible
      });
      
      // Auto-import lead files if claim has estimate_id and no photos imported yet
      if (claim.estimate_id && photos.length === 0) {
        autoImportLeadFiles(claim);
      }
    }
  }, [claim]);

  const autoImportLeadFiles = async (claimData) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/claims/${claimData.id}/attach-lead-files`,
        { lead_id: claimData.estimate_id },
        { params: { user_id: user?.id } }
      );
      if (res.data.attached_count > 0) {
        loadPhotos(); // Refresh to show imported files
      }
    } catch { /* silent — files may already be imported */ }
  };

  const loadClaim = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/claims/${claimId}`, {
        params: { user_id: user.id }
      });
      setClaim(response.data);
    } catch (error) {
      console.error('Error loading claim:', error);
      if (error.response?.status === 404) {
        navigate('/dashboard/claims');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadPhotos = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/photos`, {
        params: { user_id: user.id }
      });
      setPhotos(response.data);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      submitted: { icon: FileText, color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      pending_review: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending Review' },
      submit_supplement: { icon: Upload, color: 'bg-purple-100 text-purple-800', label: 'Submit Supplement' },
      supplement_submitted: { icon: FileText, color: 'bg-violet-100 text-violet-800', label: 'Supplement Submitted' },
      under_review: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Under Review' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Approved' },
      denied: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Denied' },
      paid: { icon: DollarSign, color: 'bg-emerald-100 text-emerald-800', label: 'Paid' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${config.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.label}
      </span>
    );
  };

  const handleExportXactimate = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/claims/${claimId}/xactimate-export`,
        {
          params: { user_id: user.id },
          responseType: 'blob',
          headers: getAuthHeaders()
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `xactimate_claim_${claim.claim_number || claimId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Xactimate CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting Xactimate:', error);
      alert(`Failed to export Xactimate CSV: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCalculate = () => {
    const { covered_work_amount, deductible, depreciation } = calculatorForm;
    
    const rcv = covered_work_amount;
    const acv = covered_work_amount - depreciation;
    const insurance_payment = Math.max(0, covered_work_amount - deductible - depreciation);
    const homeowner_responsibility = deductible;
    
    setCalculatorForm({
      ...calculatorForm,
      rcv,
      acv,
      insurance_payment,
      homeowner_responsibility
    });
  };

  const handleSaveCalculation = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/calculate`,
        {
          covered_work_amount: calculatorForm.covered_work_amount,
          deductible: calculatorForm.deductible,
          depreciation: calculatorForm.depreciation
        },
        { params: { user_id: user.id } }
      );
      
      await loadClaim();
      alert('Calculation saved successfully!');
    } catch (error) {
      console.error('Error saving calculation:', error);
      alert(`Failed to save calculation: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSendEmail = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/email-adjuster`,
        emailForm,
        { params: { user_id: user.id } }
      );
      
      setShowEmailModal(false);
      setEmailForm({
        subject: '',
        message: '',
        include_photos: true
      });
      
      alert(`Email sent successfully to ${response.data.sent_to}`);
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Failed to send email: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedStatus) return;
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/claims/${claimId}`,
        { status: selectedStatus },
        { params: { user_id: user.id } }
      );
      
      // Reload claim
      await loadClaim();
      setSelectedStatus('');
      alert('Status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleUploadEvidence = async () => {
    if (evidenceFiles.length === 0) return;
    
    setIsUploadingEvidence(true);
    try {
      for (const file of evidenceFiles) {
        const isLarge = file.size > 10 * 1024 * 1024; // >10MB use chunked
        if (isLarge) {
          // Chunked upload for large files (videos, etc.)
          const result = await chunkedUploadToS3(file, `insurance-claims/${claimId}`);
          // Register the uploaded file with the claim via a direct DB record
          await axios.post(
            `${BACKEND_URL}/api/claims/${claimId}/register-uploaded-file`,
            { url: result.url, key: result.key, file_name: file.name, file_size: file.size },
            { params: { user_id: user.id } }
          );
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('is_evidence', 'true');
          formData.append('description', 'Evidence upload');
          await axios.post(
            `${BACKEND_URL}/api/claims/${claimId}/photos`,
            formData,
            { params: { user_id: user.id }, headers: { 'Content-Type': 'multipart/form-data' } }
          );
        }
      }
      
      setEvidenceFiles([]);
      await loadPhotos();
      toast.success('Files uploaded successfully!');
    } catch (error) {
      console.error('Error uploading evidence:', error);
      toast.error(`Upload failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const handleAddDenialPlan = async () => {
    if (!denialForm.denial_reason || !denialForm.justification_strategy) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      console.log('Adding denial plan for claim:', claimId);
      console.log('Denial form data:', denialForm);
      
      // First, update status to denied (required before adding denial plan)
      const statusResponse = await axios.patch(
        `${BACKEND_URL}/api/claims/${claimId}`,
        { status: 'denied' },
        { params: { user_id: user.id } }
      );
      console.log('Status updated to denied:', statusResponse.data);
      
      // Then add denial action plan
      const denialResponse = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/denial-plan`,
        denialForm,
        { params: { user_id: user.id } }
      );
      console.log('Denial plan added successfully:', denialResponse.data);
      
      // Reload claim
      await loadClaim();
      
      // Reset form
      setDenialForm({
        denial_reason: '',
        adjuster_feedback: '',
        justification_strategy: ''
      });
      setShowDenialSection(false);
      
      alert('Denial plan added and claim resubmitted successfully!');
    } catch (error) {
      console.error('Error adding denial plan - Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert(`Failed to add denial plan: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Claim Not Found</h2>
          <p className="text-gray-600 mb-4">The claim you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/dashboard/claims')}>
            Return to Claims
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/claims')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Claims
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="w-8 h-8 mr-3 text-orange-600" />
              Claim #{claim.claim_number || 'Draft'}
            </h1>
            <p className="text-gray-600 mt-1">{claim.insurance_company}</p>
          </div>
          <div>
            {getStatusBadge(claim.status)}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Property Address</p>
          <p className="text-lg font-semibold text-gray-900">{claim.estimate?.address || 'N/A'}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Storm Date</p>
          <p className="text-lg font-semibold text-gray-900">{formatDate(claim.storm_date)}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Damage Percentage</p>
          <p className="text-lg font-semibold text-gray-900">
            {claim.damage_percentage ? `${claim.damage_percentage}%` : 'N/A'}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Est. Covered Amount</p>
          <p className="text-lg font-semibold text-orange-600">
            {formatCurrency(claim.covered_work_amount || claim.estimate?.total_cost || 0)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'photos'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Image className="w-4 h-4 inline mr-2" />
              Photos ({photos.length})
            </button>
            <button
              onClick={() => setActiveTab('weather')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'weather'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Weather Data
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'actions'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Edit className="w-4 h-4 inline mr-2" />
              Actions
            </button>
            <button
              onClick={() => setActiveTab('calculator')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'calculator'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <DollarSign className="w-4 h-4 inline mr-2" />
              Calculator
            </button>
            <button
              onClick={() => setActiveTab('submission')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'submission'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Submission Document
            </button>
            <button
              onClick={() => setActiveTab('policy')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'policy'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileSearch className="w-4 h-4 inline mr-2" />
              Policy Analysis
            </button>
            <button
              onClick={() => setActiveTab('dispute')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dispute'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="dispute-tab"
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Dispute
            </button>
            <button
              onClick={() => setActiveTab('carrier')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'carrier'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="carrier-tab"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Carrier Docs
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          {/* Insurance Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-orange-600" />
              Insurance Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Insurance Company</p>
                <p className="text-base font-semibold text-gray-900">{claim.insurance_company}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Policy Number</p>
                <p className="text-base font-semibold text-gray-900">{claim.policy_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Claim Number</p>
                <p className="text-base font-semibold text-gray-900">{claim.claim_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Policy Document</p>
                {claim.policy_document_url ? (
                  <a 
                    href={claim.policy_document_url} 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-base font-semibold text-blue-600 hover:underline flex items-center gap-1"
                    data-testid="policy-doc-link"
                  >
                    <FileText className="w-4 h-4" />
                    View Policy Document
                  </a>
                ) : (
                  <p className="text-base text-gray-400 italic">Not uploaded — use Policy Analysis tab</p>
                )}
              </div>
            </div>
          </div>

          {/* Adjuster Information */}
          {claim.adjuster && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-orange-600" />
                Adjuster Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-base font-semibold text-gray-900">{claim.adjuster.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-base font-semibold text-gray-900">{claim.adjuster.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-base font-semibold text-gray-900">{claim.adjuster.phone || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Tracking Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200 bg-blue-50">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
              Payment Tracking
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-600 block mb-1">Deposit Collected</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    defaultValue={claim.deposit_collected || ''}
                    onBlur={async (e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (value !== (claim.deposit_collected || 0)) {
                        try {
                          await axios.put(`${BACKEND_URL}/api/insurance-claims/${claimId}`, 
                            { deposit_collected: value },
                            { headers: getAuthHeaders() }
                          );
                          loadClaim();
                        } catch (err) {
                          console.error('Error updating deposit:', err);
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Deductible Due</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    defaultValue={claim.deductible_due || claim.deductible || ''}
                    onBlur={async (e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (value !== (claim.deductible_due || 0)) {
                        try {
                          await axios.put(`${BACKEND_URL}/api/insurance-claims/${claimId}`, 
                            { deductible_due: value, deductible: value },
                            { headers: getAuthHeaders() }
                          );
                          loadClaim();
                        } catch (err) {
                          console.error('Error updating deductible:', err);
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Balance Due</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    defaultValue={claim.balance_due || ''}
                    onBlur={async (e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (value !== (claim.balance_due || 0)) {
                        try {
                          await axios.put(`${BACKEND_URL}/api/insurance-claims/${claimId}`, 
                            { balance_due: value },
                            { headers: getAuthHeaders() }
                          );
                          loadClaim();
                        } catch (err) {
                          console.error('Error updating balance:', err);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Summary row */}
            <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-3 gap-4 text-center">
              <div className="bg-green-100 rounded-lg p-3">
                <p className="text-xs text-green-600">Collected</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(claim.deposit_collected || 0)}</p>
              </div>
              <div className="bg-orange-100 rounded-lg p-3">
                <p className="text-xs text-orange-600">Deductible</p>
                <p className="text-lg font-bold text-orange-700">{formatCurrency(claim.deductible_due || claim.deductible || 0)}</p>
              </div>
              <div className="bg-red-100 rounded-lg p-3">
                <p className="text-xs text-red-600">Balance Due</p>
                <p className="text-lg font-bold text-red-700">{formatCurrency(claim.balance_due || 0)}</p>
              </div>
            </div>
          </div>

          {/* Damage Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
              Damage Information
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Damage Types</p>
                <div className="flex flex-wrap gap-2">
                  {claim.damage_types?.map(type => (
                    <span key={type} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium capitalize">
                      {type.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
              {claim.damage_description && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p className="text-base text-gray-900">{claim.damage_description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Property Details */}
          {claim.estimate && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-600" />
                Property Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="text-base font-semibold text-gray-900">{claim.estimate.address}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Square Footage</p>
                  <p className="text-base font-semibold text-gray-900">
                    {claim.estimate.square_footage?.toFixed(0)} sq ft
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Original Estimate</p>
                  <p className="text-base font-semibold text-gray-900">
                    {formatCurrency(claim.estimate.total_cost)}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to={`/dashboard/estimates/${claim.estimate_id}`}
                  className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                >
                  View Full Estimate →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'photos' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Image className="w-5 h-5 mr-2 text-orange-600" />
            Damage Photos
          </h2>
          
          {/* Attach Lead Files Button */}
          {claim?.estimate_id && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Import from CRM Lead Files</p>
                <p className="text-xs text-blue-600">Attach FieldCam photos, videos, and documents from the lead file</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      `${BACKEND_URL}/api/claims/${claimId}/attach-lead-files`,
                      { lead_id: claim.estimate_id },
                      { params: { user_id: user?.id } }
                    );
                    if (res.data.attached_count > 0) {
                      toast.success(`Attached ${res.data.attached_count} files from lead`);
                      loadPhotos();
                    } else {
                      toast.info('No new files to attach');
                    }
                  } catch (err) {
                    toast.error('Failed to attach lead files');
                  }
                }}
                data-testid="attach-lead-files-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Import Files
              </Button>
            </div>
          )}
          
          {/* Photo Upload Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <h3 className="font-medium text-gray-700 mb-3 flex items-center">
              <Upload className="w-4 h-4 mr-2" />
              Upload Photos & Videos
            </h3>
            <input
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx"
              onChange={(e) => setEvidenceFiles(Array.from(e.target.files))}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-2">
              Upload damage photos, videos, inspection images, or documents. Large videos are supported.
            </p>
            
            {evidenceFiles.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Selected files: {evidenceFiles.length}
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {evidenceFiles.map((file, idx) => (
                    <li key={idx}>• {file.name}</li>
                  ))}
                </ul>
                <Button
                  onClick={handleUploadEvidence}
                  disabled={isUploadingEvidence}
                  className="mt-3 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {isUploadingEvidence ? 'Uploading...' : `Upload ${evidenceFiles.length} Photo${evidenceFiles.length > 1 ? 's' : ''}`}
                </Button>
              </div>
            )}
          </div>
          
          {/* Photos Grid */}
          {photos.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No photos uploaded yet</p>
              <p className="text-sm text-gray-400 mt-1">Use the upload section above to add photos</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, idx) => (
                <div key={photo.id} className="group relative">
                  <img
                    src={photo.supabase_url}
                    alt={photo.file_name}
                    className="w-full h-48 object-cover rounded-lg border border-gray-300 hover:border-orange-500 transition-colors cursor-pointer"
                    onClick={() => {
                      setClaimLightboxImages(photos.map(p => ({
                        url: p.supabase_url, filename: p.file_name
                      })));
                      setClaimLightboxIndex(idx);
                    }}
                    data-testid={`claim-photo-${photo.id}`}
                  />
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 truncate">{photo.file_name}</p>
                    {photo.description && (
                      <p className="text-xs text-gray-500 truncate">{photo.description}</p>
                    )}
                    {photo.is_evidence && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                        Evidence
                      </span>
                    )}
                    {photo.source === 'lead_files' && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                        From Lead
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'weather' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Weather Conditions on Storm Date {claim.storm_date && (
              <span className="ml-2 text-orange-600">
                ({new Date(claim.storm_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
              </span>
            )}
          </h2>
          {claim.weather_data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Temperature</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {claim.weather_data.conditions?.temperature}°F
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Precipitation</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {claim.weather_data.conditions?.precipitation} in
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Wind Gusts</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {claim.weather_data.conditions?.wind_gusts} mph
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-2">Weather Summary</p>
                <p className="text-base text-gray-900 p-4 bg-gray-50 rounded-lg">
                  {claim.weather_data.summary}
                </p>
              </div>

              {claim.weather_data.assessment && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Storm Severity</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {claim.weather_data.assessment.storm_severity}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Hail Likelihood</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {claim.weather_data.assessment.hail_likelihood}
                    </p>
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 mt-4">
                Data Source: {claim.weather_data.data_source || 'Open-Meteo'}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No weather data available</p>
              <p className="text-sm text-gray-400 mt-1">
                Weather data is automatically fetched when a storm date is provided
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'actions' && (
        <div className="space-y-6">
          {/* AI Field Documentation */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-lg shadow-sm border border-orange-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Mic className="w-5 h-5 mr-2 text-orange-600" />
              AI Field Documentation
              <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">NEW</span>
            </h2>
            <p className="text-gray-600 mb-4">
              Use FieldCam AI to quickly document damage on-site. Record voice notes, take photos, 
              and let AI generate a professional damage report with Xactimate codes.
            </p>
            <Link to={`/dashboard/field-cam?claim_id=${claimId}`}>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center">
                <Mic className="w-5 h-5 mr-2" />
                Launch FieldCam AI
              </Button>
            </Link>
          </div>

          {/* Export & Email Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-orange-600" />
              Export & Communication
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={handleExportXactimate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center"
              >
                <Download className="w-5 h-5 mr-2" />
                Export Xactimate CSV
              </Button>
              
              <Button
                onClick={() => setShowEmailModal(true)}
                disabled={!claim.adjuster?.email}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Adjuster
              </Button>
            </div>
            
            {!claim.adjuster?.email && (
              <p className="text-sm text-orange-600 mt-2">
                ⚠️ Add adjuster email to enable email functionality
              </p>
            )}
          </div>
          
          {/* Change Status Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Edit className="w-5 h-5 mr-2 text-orange-600" />
              Change Claim Status
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Status: {getStatusBadge(claim.status)}
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Change Status To:
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setShowDenialSection(e.target.value === 'denied');
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select new status...</option>
                  <option value="submitted">Submitted</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="submit_supplement">Submit Supplement</option>
                  <option value="supplement_submitted">Supplement Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              
              <Button
                onClick={handleStatusChange}
                disabled={!selectedStatus}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Status
              </Button>
            </div>
          </div>

          {/* Submit Supplement Section */}
          {claim.status === 'submit_supplement' && (
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h2 className="text-lg font-semibold text-purple-900 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Upload Supplement Documentation
              </h2>
              
              <p className="text-sm text-purple-800 mb-4">
                Upload additional images, documents, and evidence to support your supplement submission.
              </p>
              
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
                    <Image className="w-4 h-4 mr-2" />
                    Upload Additional Files
                  </h3>
                  
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setEvidenceFiles(Array.from(e.target.files))}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Upload additional damage photos, videos, or documentation
                  </p>
                  
                  {evidenceFiles.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Selected files: {evidenceFiles.length}
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {evidenceFiles.map((file, idx) => (
                          <li key={idx}>• {file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleUploadEvidence}
                    disabled={evidenceFiles.length === 0 || isUploadingEvidence}
                    className="mt-3 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {isUploadingEvidence ? 'Uploading...' : 'Upload Files'}
                  </Button>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">
                    Ready to Submit?
                  </h3>
                  <p className="text-sm text-green-800 mb-3">
                    Once all additional documentation is uploaded, mark as submitted to send to adjuster.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.patch(
                          `${BACKEND_URL}/api/claims/${claimId}`,
                          { status: 'supplement_submitted' },
                          { params: { user_id: user.id } }
                        );
                        await loadClaim();
                        alert('Supplement marked as submitted!');
                      } catch (error) {
                        console.error('Error updating status:', error);
                        alert('Failed to update status');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark Supplement as Submitted
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Denial Action Plan Section */}
          {showDenialSection && (
            <div className="bg-red-50 p-6 rounded-lg border border-red-200">
              <h2 className="text-lg font-semibold text-red-900 mb-4 flex items-center">
                <XCircle className="w-5 h-5 mr-2" />
                Add Denial Action Plan
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Denial Reason *
                  </label>
                  <input
                    type="text"
                    value={denialForm.denial_reason}
                    onChange={(e) => setDenialForm({...denialForm, denial_reason: e.target.value})}
                    placeholder="e.g., Insufficient evidence"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adjuster Feedback
                  </label>
                  <textarea
                    value={denialForm.adjuster_feedback}
                    onChange={(e) => setDenialForm({...denialForm, adjuster_feedback: e.target.value})}
                    placeholder="What did the adjuster say?"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Justification Strategy *
                  </label>
                  <textarea
                    value={denialForm.justification_strategy}
                    onChange={(e) => setDenialForm({...denialForm, justification_strategy: e.target.value})}
                    placeholder="How will you appeal this?"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                {/* Evidence Upload */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-3 flex items-center">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Additional Evidence
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Upload photos or documents to support your resubmission
                  </p>
                  
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    onChange={(e) => setEvidenceFiles(Array.from(e.target.files))}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                  />
                  
                  {evidenceFiles.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Selected files: {evidenceFiles.length}
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {evidenceFiles.map((file, idx) => (
                          <li key={idx}>• {file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleUploadEvidence}
                    disabled={evidenceFiles.length === 0 || isUploadingEvidence}
                    className="mt-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {isUploadingEvidence ? 'Uploading...' : 'Upload Evidence'}
                  </Button>
                </div>
                
                <Button
                  onClick={handleAddDenialPlan}
                  disabled={!denialForm.denial_reason || !denialForm.justification_strategy}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50"
                >
                  Add Denial Plan & Resubmit
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'calculator' && (
        <div className="space-y-6">
          {/* Calculator Introduction */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h2 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Insurance Claim Calculator
            </h2>
            <p className="text-sm text-blue-800">
              Calculate insurance payment amounts, depreciation, and homeowner responsibility.
            </p>
          </div>

          {/* Input Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Input Values</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Covered Work Amount (RCV)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={calculatorForm.covered_work_amount}
                    onChange={(e) => {
                      setCalculatorForm({
                        ...calculatorForm,
                        covered_work_amount: parseFloat(e.target.value) || 0
                      });
                    }}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    step="0.01"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Replacement Cost Value - Full cost to repair
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deductible
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={calculatorForm.deductible}
                    onChange={(e) => {
                      setCalculatorForm({
                        ...calculatorForm,
                        deductible: parseFloat(e.target.value) || 0
                      });
                    }}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    step="0.01"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Amount homeowner pays out of pocket
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Depreciation
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={calculatorForm.depreciation}
                    onChange={(e) => {
                      setCalculatorForm({
                        ...calculatorForm,
                        depreciation: parseFloat(e.target.value) || 0
                      });
                    }}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    step="0.01"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Age-related value reduction
                </p>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={handleCalculate}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg font-semibold"
              >
                Calculate Amounts
              </Button>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Calculation Results</h3>
            
            <div className="space-y-4">
              {/* RCV */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Replacement Cost Value (RCV)</p>
                  <p className="text-xs text-blue-500">Full cost to replace/repair damage</p>
                </div>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(calculatorForm.rcv)}
                </p>
              </div>

              {/* ACV */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Actual Cash Value (ACV)</p>
                  <p className="text-xs text-purple-500">RCV minus depreciation</p>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {formatCurrency(calculatorForm.acv)}
                </p>
              </div>

              {/* Deductible */}
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Homeowner Deductible</p>
                  <p className="text-xs text-orange-500">Amount homeowner pays</p>
                </div>
                <p className="text-2xl font-bold text-orange-900">
                  {formatCurrency(calculatorForm.homeowner_responsibility)}
                </p>
              </div>

              {/* Insurance Payment */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border-2 border-green-300">
                <div>
                  <p className="text-sm text-green-600 font-bold">Insurance Company Pays</p>
                  <p className="text-xs text-green-500">ACV minus deductible</p>
                </div>
                <p className="text-3xl font-bold text-green-900">
                  {formatCurrency(calculatorForm.insurance_payment)}
                </p>
              </div>
            </div>

            {/* Formula Explanation */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Calculation Formula:</h4>
              <div className="text-sm text-gray-700 space-y-1">
                <p>• <strong>RCV</strong> = Covered Work Amount</p>
                <p>• <strong>ACV</strong> = RCV - Depreciation = {formatCurrency(calculatorForm.rcv)} - {formatCurrency(calculatorForm.depreciation)} = {formatCurrency(calculatorForm.acv)}</p>
                <p>• <strong>Insurance Payment</strong> = ACV - Deductible = {formatCurrency(calculatorForm.acv)} - {formatCurrency(calculatorForm.deductible)} = {formatCurrency(calculatorForm.insurance_payment)}</p>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={handleSaveCalculation}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold flex items-center"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Save Calculation to Claim
              </Button>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Breakdown</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-700">Total Repair Cost (RCV)</span>
                <span className="font-semibold text-gray-900">{formatCurrency(calculatorForm.rcv)}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-700">Less: Depreciation</span>
                <span className="font-semibold text-red-600">-{formatCurrency(calculatorForm.depreciation)}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-700">Actual Cash Value (ACV)</span>
                <span className="font-semibold text-gray-900">{formatCurrency(calculatorForm.acv)}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-700">Less: Deductible</span>
                <span className="font-semibold text-red-600">-{formatCurrency(calculatorForm.deductible)}</span>
              </div>
              
              <div className="flex items-center justify-between py-3 bg-green-50 px-4 rounded-lg">
                <span className="text-lg font-bold text-green-900">Net Insurance Payment</span>
                <span className="text-2xl font-bold text-green-600">{formatCurrency(calculatorForm.insurance_payment)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission Document Tab */}
      {activeTab === 'submission' && (
        <SubmissionDocumentTab claim={claim} user={user} />
      )}

      {/* Policy Analysis Tab */}
      {activeTab === 'policy' && (
        <PolicyAnalysisTab 
          claimId={claimId} 
          userId={user?.id}
          claim={claim}
        />
      )}

      {/* Dispute Narrative Tab */}
      {activeTab === 'dispute' && (
        <DisputeNarrativeTab
          claimId={claimId}
          userId={user?.id}
          claim={claim}
        />
      )}

      {/* Carrier Documents Tab */}
      {activeTab === 'carrier' && (
        <CarrierDocumentsTab
          claimId={claimId}
          userId={user?.id}
          claim={claim}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Mail className="w-6 h-6 mr-2 text-orange-600" />
                Email Insurance Adjuster
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To: {claim.adjuster?.name || 'Adjuster'}
                </label>
                <input
                  type="text"
                  value={claim.adjuster?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  placeholder={`Insurance Claim #${claim.claim_number || claimId}`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                  placeholder="Add your message to the adjuster..."
                  rows="6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Claim details and property information will be automatically included
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={emailForm.include_photos}
                    onChange={(e) => setEmailForm({...emailForm, include_photos: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Include damage photos (up to 10)</span>
                </label>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Email will include:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Claim information (claim #, policy #, property address)</li>
                  <li>✓ Damage details and description</li>
                  <li>✓ Weather data (if available)</li>
                  <li>✓ Covered work amount and deductible</li>
                  {emailForm.include_photos && <li>✓ Damage photos</li>}
                </ul>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  onClick={() => setShowEmailModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendEmail}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {claimLightboxImages.length > 0 && (
        <ImageLightbox
          images={claimLightboxImages}
          initialIndex={claimLightboxIndex}
          onClose={() => setClaimLightboxImages([])}
        />
      )}
    </div>
  );
};

export default ClaimDetail;
